import classNames from "classnames";
import React, { memo, useEffect, useMemo, useRef } from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { TextSizeValue } from "../../theme/typography";
import { DisabledTooltip } from "../../tooltip/disabled-tooltip";
import * as toggleCss from "./redo-toggle.module.css";

export const redoToggleSize = ["xs", "sm", "md"] as const;
export type RedoToggleSize = (typeof redoToggleSize)[number];

export const redoToggleStyle = ["desktop", "mobile"] as const;
export type RedoToggleStyle = (typeof redoToggleStyle)[number];

export interface RedoToggleProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "style"
> {
  value: boolean;
  setValue(value: boolean): void;
  style?: RedoToggleStyle;
  size?: RedoToggleSize;
  label?: string;
  description?: string;
  disabled?: string | boolean;
  labelPosition?: "left" | "right";
  testId?: string;
  semantic?: boolean;
  indeterminate?: boolean;
}

export const RedoToggle = memo(function RedoToggle({
  value,
  setValue,
  size = "xs",
  style = "desktop",
  label,
  description,
  disabled,
  labelPosition = "right",
  testId,
  semantic = false,
  indeterminate = false,
  ...rest
}: RedoToggleProps) {
  return (
    <DisabledTooltip disabledMessage={disabled}>
      {style === "desktop" ? (
        <DesktopToggle
          description={description}
          disabled={disabled}
          indeterminate={indeterminate}
          label={label}
          labelPosition={labelPosition}
          semantic={semantic}
          setValue={setValue}
          size={size}
          testId={testId}
          value={value}
          {...rest}
        />
      ) : (
        <MobileToggle
          disabled={disabled}
          label={label}
          semantic={semantic}
          setValue={setValue}
          size={size}
          testId={testId}
          value={value}
          {...rest}
        />
      )}
    </DisabledTooltip>
  );
});

export const ToggleSwitch = memo(function ToggleSwitch({
  value,
  setValue,
  disabled,
  size,
  semantic = false,
  indeterminate = false,
}: {
  value: boolean;
  disabled: string | boolean | undefined;
  setValue: (value: boolean) => void;
  size: RedoToggleSize;
  semantic?: boolean;
  indeterminate?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <>
      <input
        aria-checked={indeterminate ? "mixed" : value}
        checked={value}
        className={classNames(toggleCss.toggleInput)}
        disabled={!!disabled}
        onChange={(e) => setValue(e.target.checked)}
        ref={inputRef}
        type="checkbox"
      />
      <div
        className={classNames(
          toggleCss.toggleSwitch,
          toggleSizeToClass[size],
          semantic && toggleCss.semantic,
          indeterminate && toggleCss.indeterminate,
        )}
      />
    </>
  );
});

const DesktopToggle = memo(function DesktopToggle({
  value,
  setValue,
  size = "xs",
  label,
  description,
  disabled,
  labelPosition,
  testId,
  semantic = false,
  indeterminate = false,
  ...rest
}: Omit<RedoToggleProps, "style">) {
  const showDescriptors = !!(label || description);

  const labelTextColor =
    (disabled && "disabled") || (value && "primary") || "secondary";
  const descriptionTextColor = (disabled && "disabled") || "secondary";

  const onlyLabel = !description && !!label;

  const descriptors = useMemo(
    () =>
      showDescriptors && (
        <Flex dir="column" gap="none">
          {label && (
            <Text
              className={toggleCss.text}
              fontSize={toggleSizeToLabelSize[size]}
              fontWeight={value ? "medium" : "regular"}
              textAlign={labelPosition === "left" ? "right" : "left"}
              textColor={labelTextColor}
            >
              {label}
            </Text>
          )}
          {description && (
            <Text
              className={toggleCss.text}
              fontSize={toggleSizeToDescriptionSize[size]}
              textAlign={labelPosition === "left" ? "right" : "left"}
              textColor={descriptionTextColor}
            >
              {description}
            </Text>
          )}
        </Flex>
      ),
    [
      showDescriptors,
      label,
      description,
      size,
      value,
      labelTextColor,
      descriptionTextColor,
      labelPosition,
    ],
  );

  return (
    <Flex
      align={onlyLabel ? "center" : "stretch"}
      as="label"
      data-arbiter="RedoToggle"
      data-testid={testId}
      {...rest}
    >
      {labelPosition === "left" && descriptors}
      <Flex pt={showDescriptors ? "xxs" : "none"}>
        <ToggleSwitch
          disabled={disabled}
          indeterminate={indeterminate}
          semantic={semantic}
          setValue={setValue}
          size={size}
          value={value}
        />
      </Flex>
      {labelPosition === "right" && descriptors}
    </Flex>
  );
});

const MobileToggle = memo(function MobileToggle({
  value,
  setValue,
  size = "xs",
  label,
  disabled,
  testId,
  semantic = false,
  indeterminate = false,
  ...rest
}: Omit<RedoToggleProps, "style" | "description">) {
  const labelTextColor =
    (disabled && "disabled") || (value && "primary") || "secondary";

  return (
    <Flex
      as="label"
      data-arbiter="RedoToggle"
      data-testid={testId}
      grow={1}
      justify="space-between"
      {...rest}
    >
      <ToggleSwitch
        disabled={disabled}
        indeterminate={indeterminate}
        semantic={semantic}
        setValue={setValue}
        size={size}
        value={value}
      />
      {label && (
        <Text
          className={toggleCss.text}
          fontSize={toggleSizeToLabelSize[size]}
          fontWeight={value ? "medium" : "regular"}
          textColor={labelTextColor}
        >
          {label}
        </Text>
      )}
    </Flex>
  );
});

const toggleSizeToClass: Record<RedoToggleSize, string> = {
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  xs: toggleCss.xs,
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  sm: toggleCss.sm,
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  md: toggleCss.md,
};
const toggleSizeToLabelSize: Record<RedoToggleSize, TextSizeValue> = {
  xs: "xs",
  sm: "sm",
  md: "md",
};

const toggleSizeToDescriptionSize: Record<RedoToggleSize, TextSizeValue> = {
  xs: "xxs",
  sm: "xs",
  md: "sm",
};
